import { Play, Pause, SkipBack, SkipForward, X, Volume2, Minimize2, Maximize2 } from "lucide-react";
import { useVideoPlayer } from "@/context/VideoPlayerContext";

/**
 * GlobalVideoPlayer — single YouTube iframe at App root that survives BOTH
 * route changes AND mode toggles (expanded <-> minimized). The critical
 * invariant: the <iframe> element occupies the SAME position in the JSX tree
 * regardless of mode, so React never unmounts/remounts it. Only its wrapper
 * classes change.
 */
const GlobalVideoPlayer = () => {
  const {
    current,
    queue,
    idx,
    isMinimized,
    isPlaying,
    audioOn,
    iframeRef,
    next,
    prev,
    toggle,
    enableAudio,
    minimize,
    expand,
    close,
  } = useVideoPlayer();

  if (!current) return null;

  // YouTube IFrame API requires `origin` matching the parent page for reliable
  // postMessage + playback continuity, especially on iOS Safari when the DOM
  // updates around the iframe. Falls back to '' during SSR / early paint.
  const originParam = typeof window !== "undefined" ? window.location.origin : "";
  const iframeSrc = `https://www.youtube.com/embed/${current.youtube_id}?autoplay=1&mute=1&enablejsapi=1&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3&origin=${encodeURIComponent(originParam)}`;

  // Layer classes — the ONLY thing that changes between modes.
  const outerClass = isMinimized
    ? "fixed bottom-24 right-3 z-40 w-64 sm:w-72"
    : "fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm";
  const cardClass = isMinimized
    ? "rounded-2xl overflow-hidden bg-black shadow-[0_20px_50px_rgba(0,0,0,0.4)] border border-white/10 w-full"
    : "rounded-2xl overflow-hidden bg-black shadow-[0_20px_60px_rgba(0,0,0,0.5)] w-full max-w-4xl";
  const barClass = isMinimized
    ? "p-2 bg-[var(--fi-blue)] text-white flex items-center gap-2"
    : "p-4 sm:p-5 bg-[var(--fi-blue)] text-white flex items-center gap-3";

  return (
    <div
      data-testid={isMinimized ? "global-video-mini" : "global-video-expanded"}
      className={outerClass}
      style={
        isMinimized
          ? { paddingBottom: "env(safe-area-inset-bottom, 0)" }
          : { paddingTop: "env(safe-area-inset-top, 0)", paddingBottom: "env(safe-area-inset-bottom, 0)" }
      }
    >
      <div className={cardClass}>
        {/* Iframe — SAME JSX position in both modes. This is what keeps
            playback continuous when the user toggles minimize/expand. */}
        <div className="relative aspect-video w-full">
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            title={current.title}
            className={`w-full h-full ${isMinimized ? "pointer-events-none" : ""}`}
            allow="autoplay; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            data-testid="global-video-iframe"
          />

          {isMinimized ? (
            <>
              {/* Tap-to-expand overlay + close */}
              <button
                onClick={expand}
                data-testid="global-video-expand"
                className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/25 transition-colors group"
                aria-label="Expandir player"
              >
                <Maximize2 className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
              <button
                onClick={close}
                data-testid="global-video-close-mini"
                className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur flex items-center justify-center text-white z-10"
                aria-label="Fechar"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <>
              {!audioOn && (
                <button
                  onClick={enableAudio}
                  data-testid="global-video-enable-audio"
                  className="absolute bottom-4 right-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[var(--fi-red)] hover:bg-[var(--fi-red-hover)] text-white font-bold uppercase tracking-widest text-xs shadow-[0_8px_24px_rgba(215,38,56,0.45)]"
                  aria-label="Ativar áudio"
                >
                  <Volume2 className="w-4 h-4" />
                  Ativar áudio
                </button>
              )}
              <div className="absolute top-3 right-3 flex gap-2">
                <button
                  onClick={minimize}
                  data-testid="global-video-minimize"
                  className="w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur flex items-center justify-center text-white transition-colors"
                  aria-label="Minimizar player"
                  title="Minimizar (continua tocando ao navegar)"
                >
                  <Minimize2 className="w-4 h-4" />
                </button>
                <button
                  onClick={close}
                  data-testid="global-video-close"
                  className="w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur flex items-center justify-center text-white transition-colors"
                  aria-label="Fechar player"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>

        {/* Bottom controls bar — different chrome per mode */}
        <div className={barClass}>
          {isMinimized ? (
            <>
              <button
                onClick={toggle}
                data-testid="global-video-toggle-mini"
                className="w-8 h-8 rounded-full bg-[var(--fi-red)] flex items-center justify-center hover:bg-[var(--fi-red-hover)] transition-colors shrink-0"
                aria-label={isPlaying ? "Pausar" : "Tocar"}
              >
                {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
              </button>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-widest text-white/60 font-bold truncate">Tocando</div>
                <div className="text-xs font-bold truncate">{current.title}</div>
              </div>
              <button
                onClick={next}
                data-testid="global-video-next-mini"
                className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center shrink-0"
                aria-label="Próxima"
              >
                <SkipForward className="w-3 h-3" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={prev}
                data-testid="global-video-prev"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                aria-label="Faixa anterior"
              >
                <SkipBack className="w-4 h-4" />
              </button>
              <button
                onClick={toggle}
                data-testid="global-video-toggle"
                className="w-12 h-12 rounded-full bg-[var(--fi-red)] hover:bg-[var(--fi-red-hover)] flex items-center justify-center transition-colors"
                aria-label={isPlaying ? "Pausar" : "Tocar"}
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </button>
              <button
                onClick={next}
                data-testid="global-video-next"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                aria-label="Próxima faixa"
              >
                <SkipForward className="w-4 h-4" />
              </button>
              <div className="flex-1 min-w-0 ml-2">
                <div className="text-[11px] uppercase tracking-widest text-white/60 font-bold">
                  Tocando agora ({idx + 1} de {queue.length}) {current.genre && <span className="ml-1 text-white/80">· {current.genre}</span>}
                </div>
                <div className="font-display text-lg sm:text-xl font-extrabold uppercase truncate">
                  {current.title}
                </div>
                <div className="text-white/70 text-sm truncate">{current.artist}</div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalVideoPlayer;
